import type { IrsStatus, TaxYear } from '@divorceos/schema';
import y2022 from '../data/2022.json';
import y2023 from '../data/2023.json';
import y2024 from '../data/2024.json';
import y2025 from '../data/2025.json';
import y2026 from '../data/2026.json';
import y2027 from '../data/2027.json';

/** A number per IRS filing status. */
export type ByStatus = Record<IrsStatus, number>;
/** A number that differs only for joint returns. */
export interface ByJoint { MFJ: number; other: number }
/** A number that differs only for MFS. */
export interface ByMfs { default: number; MFS: number }

export interface EicParameterSet {
  children: number;
  maxCredit: number;
  phaseInEnd: number;
  phaseOutStart: ByJoint;
  phaseOutEnd: ByJoint;
}

/** Shape of packages/tax-us/data/<year>.json. Every year-specific figure lives there. */
export interface YearData {
  year: number;
  source: string;
  verified: boolean;
  notes: string[];
  /** Ordinary rates, one more than bracketTops (the last rate applies above the last top). */
  rates: number[];
  bracketTops: Record<IrsStatus, number[]>;
  standardDeduction: ByStatus;
  /** Extra standard deduction per 65/blind box. */
  additionalStandardDeduction: { married: number; unmarried: number };
  /** 2025+ (P.L. 119-21): deduction per taxpayer 65+, phased out on MAGI. */
  seniorDeduction?: { amount: number; phaseOutStart: ByJoint; phaseOutRate: number };
  capitalGains: {
    zeroRateTop: ByStatus;
    fifteenRateTop: ByStatus;
    rate15: number;
    rate20: number;
    rateUnrecaptured1250: number;
    rate28: number;
  };
  capitalLossLimit: ByMfs;
  scheduleA: {
    medicalFloor: number;
    saltCap: ByMfs;
    /** 2025+ (P.L. 119-21): cap reduced by `rate` x (MAGI - start), not below floor. */
    saltCapPhaseDown?: { start: ByMfs; rate: number; floor: ByMfs };
  };
  amt: {
    exemption: ByStatus;
    phaseOutStart: ByStatus;
    phaseOutRate: number;
    lowRate: number;
    highRate: number;
    highRateBreakpoint: ByStatus;
  };
  employment: {
    ssWageBase: number;
    seNetEarningsFactor: number;
    seThreshold: number;
    seSsRate: number;
    seMedicareRate: number;
    ficaSsRate: number;
    ficaMedicareRate: number;
    additionalMedicareRate: number;
    additionalMedicareThreshold: ByStatus;
    niitRate: number;
    niitThreshold: ByStatus;
  };
  childTaxCredit: {
    perChild: number;
    perOtherDependent: number;
    phaseOutStart: ByStatus;
    /** Reduction per $1,000 (or fraction) of AGI over the start (50 = 5%). */
    phaseOutPer1000: number;
    actcPerChild: number;
    actcRate: number;
    actcEarnedIncomeFloor: number;
  };
  childCareCredit: {
    capOne: number;
    capTwoPlus: number;
    maxRate: number;
    minRate: number;
    agiStart: number;
    stepSize: number;
    stepRate: number;
  };
  socialSecurityBenefits: { base: ByJoint; secondTier: ByJoint; rate1: number; rate2: number };
  eic: { investmentIncomeLimit: number; byChildren: EicParameterSet[] };
  withholding: {
    w4StandardAllowance: ByJoint;
    rates: number[];
    bracketTops: Record<IrsStatus, number[]>;
  };
}

const STATUSES: IrsStatus[] = ['MFJ', 'HOH', 'SINGLE', 'MFS'];

function checkByStatus(v: unknown, what: string): void {
  const o = v as Record<string, unknown>;
  for (const s of STATUSES) {
    if (typeof o?.[s] !== 'number') throw new Error(`tax-us data: ${what}.${s} missing`);
  }
}

/** Minimal structural validation so a malformed data file fails loudly at load time. */
function validate(d: YearData): YearData {
  if (d.rates.length < 2) throw new Error('tax-us data: rates');
  for (const s of STATUSES) {
    if (d.bracketTops[s].length !== d.rates.length - 1) throw new Error(`tax-us data ${d.year}: bracketTops.${s} length`);
    if (d.withholding.bracketTops[s].length !== d.withholding.rates.length - 1) throw new Error(`tax-us data ${d.year}: withholding.bracketTops.${s} length`);
    const tops = d.bracketTops[s];
    for (let i = 1; i < tops.length; i++) if (tops[i] <= tops[i - 1]) throw new Error(`tax-us data ${d.year}: bracketTops.${s} not increasing`);
  }
  checkByStatus(d.standardDeduction, 'standardDeduction');
  checkByStatus(d.capitalGains.zeroRateTop, 'capitalGains.zeroRateTop');
  checkByStatus(d.capitalGains.fifteenRateTop, 'capitalGains.fifteenRateTop');
  checkByStatus(d.amt.exemption, 'amt.exemption');
  checkByStatus(d.amt.phaseOutStart, 'amt.phaseOutStart');
  checkByStatus(d.amt.highRateBreakpoint, 'amt.highRateBreakpoint');
  checkByStatus(d.employment.additionalMedicareThreshold, 'employment.additionalMedicareThreshold');
  checkByStatus(d.employment.niitThreshold, 'employment.niitThreshold');
  checkByStatus(d.childTaxCredit.phaseOutStart, 'childTaxCredit.phaseOutStart');
  if (d.eic.byChildren.length !== 4) throw new Error(`tax-us data ${d.year}: eic.byChildren must have 4 sets (0..3+)`);
  return d;
}

const TABLES: Record<TaxYear, YearData> = {
  2022: validate(y2022 as YearData),
  2023: validate(y2023 as YearData),
  2024: validate(y2024 as YearData),
  2025: validate(y2025 as YearData),
  2026: validate(y2026 as YearData),
  2027: validate(y2027 as YearData),
};

/**
 * Which figures to use: 'irs' = the published IRS tables in data/<year>.json; 'xspouse' = what the original
 * us_tax.dll holds (notes/us_tax.md §2, §4.7): the ACTC per-child cap hard-coded at 1,500 for every year and,
 * for 2024, 2023 figures x 1.032 with the DLL's rounding instead of the real 2024 numbers. Compat mode only.
 */
export type FederalTables = 'irs' | 'xspouse';

const XSPOUSE_CACHE: Partial<Record<TaxYear, YearData>> = {};
const STATUS_KEYS: IrsStatus[] = ['MFJ', 'HOH', 'SINGLE', 'MFS'];
const mapBy = (o: ByStatus, f: (v: number) => number): ByStatus =>
  Object.fromEntries(STATUS_KEYS.map((k) => [k, f(o[k])])) as ByStatus;
const floor50 = (x: number) => Math.floor(x / 50) * 50;
const round100 = (x: number) => Math.round(x / 100) * 100;
const round50 = (x: number) => Math.round(x / 50) * 50;
const round10 = (x: number) => Math.round(x / 10) * 10;

/** us_tax.dll tables for `year` (2024 = 2023 x FUN_10001206 = 1.032, rounded per item as in notes/us_tax.md §4). */
function xspouseYearData(year: TaxYear): YearData {
  const cached = XSPOUSE_CACHE[year];
  if (cached) return cached;
  const base = TABLES[year === 2024 ? 2023 : year];
  if (!base || year >= 2025) throw new Error(`tax-us: the original engine has no tables for tax year ${year}`);
  const f = year === 2024 ? 1.032 : 1;
  const d: YearData = {
    ...base,
    year,
    source: f === 1 ? `${base.source} [xspouse compat: ACTC 1,500]` : 'us_tax.dll projection: 2023 x 1.032 (notes/us_tax.md §2)',
    childTaxCredit: { ...base.childTaxCredit, actcPerChild: 1500 },
  };
  if (f !== 1) {
    d.bracketTops = Object.fromEntries(STATUS_KEYS.map((k) => [k, base.bracketTops[k].map((t) => t * f)])) as Record<IrsStatus, number[]>;
    d.standardDeduction = mapBy(base.standardDeduction, (v) => floor50(v * f));
    d.capitalGains = {
      ...base.capitalGains,
      zeroRateTop: mapBy(base.capitalGains.zeroRateTop, (v) => floor50(v * f)),
      fifteenRateTop: mapBy(base.capitalGains.fifteenRateTop, (v) => floor50(v * f)),
    };
    d.amt = {
      ...base.amt,
      exemption: { MFJ: round100(base.amt.exemption.MFJ * f + 0.5), HOH: round100(base.amt.exemption.HOH * f), SINGLE: round100(base.amt.exemption.SINGLE * f), MFS: round50(base.amt.exemption.MFS * f) },
      phaseOutStart: mapBy(base.amt.phaseOutStart, (v) => v * f),
      highRateBreakpoint: mapBy(base.amt.highRateBreakpoint, (v) => v * f),
    };
    d.employment = { ...base.employment, ssWageBase: round100(base.employment.ssWageBase * f + 0.5) };
    d.eic = {
      investmentIncomeLimit: round10(base.eic.investmentIncomeLimit * f + 0.5),
      byChildren: base.eic.byChildren.map((e) => ({
        ...e,
        maxCredit: round10(e.maxCredit * f + 0.5), phaseInEnd: round10(e.phaseInEnd * f + 0.5),
        phaseOutStart: { MFJ: round10(e.phaseOutStart.MFJ * f + 0.5), other: round10(e.phaseOutStart.other * f + 0.5) },
        phaseOutEnd: { MFJ: round10(e.phaseOutEnd.MFJ * f + 0.5), other: round10(e.phaseOutEnd.other * f + 0.5) },
      })),
    };
    d.withholding = {
      ...base.withholding,
      bracketTops: Object.fromEntries(STATUS_KEYS.map((k) => [k, base.withholding.bracketTops[k].map((t) => t * f)])) as Record<IrsStatus, number[]>,
    };
  }
  XSPOUSE_CACHE[year] = d;
  return d;
}

/** Year tables. Throws for an unsupported year. */
export function yearData(year: TaxYear, tables: FederalTables = 'irs'): YearData {
  if (tables === 'xspouse') return xspouseYearData(year);
  const d = TABLES[year];
  if (!d) throw new Error(`tax-us: no tables for tax year ${year}`);
  return d;
}

export const SUPPORTED_YEARS: TaxYear[] = [2022, 2023, 2024, 2025];

export function byJoint(v: ByJoint, status: IrsStatus): number {
  return status === 'MFJ' ? v.MFJ : v.other;
}
export function byMfs(v: ByMfs, status: IrsStatus): number {
  return status === 'MFS' ? v.MFS : v.default;
}
